import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import config from '../../config';
import { formatApiError, normalizeUploadError, readApiJson } from '../../utilities/apiErrorMessages';

export const fetchProfile = createAsyncThunk(
  'profile/fetchProfile',
  async (token, { rejectWithValue }) => {
    try {
      const base = (config.apiUrl || '').replace(/\/$/, '')
      const endpoint = base.endsWith('/vendor') ? `${base}/profile` : `${base}/vendor/profile`
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data ?? { message: 'Unable to load profile' });
      }

      const user = data?.user ?? data?.data ?? null;

      if (!user) {
        return rejectWithValue('Profile payload is empty');
      }

      return user;
    } catch (error) {
      return rejectWithValue(error?.message ?? 'Unexpected error');
    }
  }
);

export const updateProfile = createAsyncThunk(
  'profile/updateProfile',
  async ({ token, values, avatarFile }, { rejectWithValue, dispatch }) => {
    try {
      const base = (config.apiUrl || '').replace(/\/$/, '')
      const endpoint = base.endsWith('/vendor') ? `${base}/profile` : `${base}/vendor/profile`
      const headers = {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      };

      let response;
      if (avatarFile) {
        const fd = new FormData();
        Object.entries(values ?? {}).forEach(([key, value]) => {
          if (value === undefined || value === null) {
            return;
          }
          if (typeof value === 'boolean') {
            fd.append(key, value ? '1' : '0');
            return;
          }
          fd.append(key, String(value));
        });
        fd.append('avatar', avatarFile);
        response = await fetch(endpoint, {
          method: 'POST',
          headers,
          body: fd,
        });
      } else {
        response = await fetch(endpoint, {
          method: 'PUT',
          headers: {
            ...headers,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(values),
        });
      }

      const data = await readApiJson(response);

      if (!response.ok) {
        return rejectWithValue(normalizeUploadError(response, data, 'Unable to update profile'));
      }

      const user = data?.user ?? data?.data ?? null;

      if (!user) {
        return rejectWithValue('Profile update returned empty payload');
      }

      // Update stored user in localStorage via LoginSlice authentication
      dispatch({ type: 'login/authentication', payload: { token, user } });

      return { user, message: data?.message ?? 'Profile updated' };
    } catch (error) {
      return rejectWithValue(error?.message ?? 'Unexpected error');
    }
  }
);

export const updatePassword = createAsyncThunk(
  'profile/updatePassword',
  async ({ token, values }, { rejectWithValue }) => {
    try {
      const base = (config.apiUrl || '').replace(/\/$/, '')
      const endpoint = base.endsWith('/vendor') ? `${base}/password` : `${base}/vendor/password`
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(values),
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data ?? { message: 'Unable to update password' });
      }

      return { message: data?.message ?? 'Password updated' };
    } catch (error) {
      return rejectWithValue(error?.message ?? 'Unexpected error');
    }
  }
);

const initialState = {
  user: null,
  loading: false,
  error: null,
  successMessage: null,
};

const ProfileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    clearProfileError: (state) => {
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(fetchProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = formatApiError(action.payload, action.error?.message ?? null);
      })

      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.successMessage = action.payload.message ?? 'Profile updated';
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = formatApiError(action.payload, action.error?.message ?? null);
      })

      .addCase(updatePassword.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(updatePassword.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message ?? 'Password updated';
      })
      .addCase(updatePassword.rejected, (state, action) => {
        state.loading = false;
        state.error = formatApiError(action.payload, action.error?.message ?? null);
      });
  },
});

export const { clearProfileError } = ProfileSlice.actions;
export default ProfileSlice.reducer;
